import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { AuthShell } from "@/components/AuthShell";
import { useSession } from "@/hooks/useSession";
import { guestApi } from "@/lib/api";

const unwrapCustomer = (res) =>
  res?.unwrapped?.customer ||
  res?.unwrapped?.user ||
  res?.unwrapped ||
  res?.data?.data?.customer ||
  res?.data?.customer ||
  res?.data?.user ||
  res?.data;

export const Login = () => {
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/account";
  const navigate = useNavigate();
  const { login } = useSession();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError("Please enter a valid email.");
      return;
    }

    if (password.length < 6) {
      setError("Password should be at least 6 characters.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const res = await guestApi.login({
        email,
        password,
      });

      if (
        res?.status === 202 ||
        res?.unwrapped?.verification_required
      ) {
        toast.info(
          res?.message || "Email verification is required.",
        );

        navigate(
          `/verify-email-otp?email=${encodeURIComponent(email)}`,
        );

        return;
      }

      const loginCustomer = unwrapCustomer(res);

      if (!loginCustomer?.email) {
        throw new Error(
          "The login response did not include the guest profile.",
        );
      }

      /*
       * Verify that Laravel/Sanctum actually persisted the
       * authenticated session before entering protected routes.
       *
       * This prevents:
       *
       * login
       *   -> /account
       *   -> session check fails
       *   -> /login
       *
       * when the browser cookie/session was not established.
       */
      const meRes = await guestApi.me();
      const customer = unwrapCustomer(meRes);

      if (!customer?.email) {
        throw new Error(
          "The authenticated guest session could not be verified.",
        );
      }

      login({
        id: customer.id,
        email: customer.email,
        name: customer.name || "Guest",
        emailVerified: customer.email_verified_at != null,
        phone: customer.phone || "",
        avatar: customer.avatar_url || customer.avatar || "",
      });

      toast.success("Welcome back.");

      navigate(redirect, {
        replace: true,
      });
    } catch (err) {
      const status = err?.response?.status;

      setError(
        err?.response?.data?.message ||
          err?.apiMessage ||
          (status === 401 || status === 419
            ? "Login succeeded but the browser session could not be established. Please refresh and try again."
            : err?.response
              ? "Invalid email or password."
              : "Unable to connect to StayEasy. Please try again."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Log in · Guest access"
      title={
        <>
          Welcome
          <br />
          back.
        </>
      }
      subtitle="Sign in to continue your booking or check on an upcoming stay."
    >
      <form
        onSubmit={onSubmit}
        className="space-y-6"
        data-testid="login-form"
      >
        <Field
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          testId="login-email"
        />

        <div>
          <Field
            label="Password"
            type={showPw ? "text" : "password"}
            value={password}
            onChange={setPassword}
            testId="login-password"
            rightSlot={
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={
                  showPw ? "Hide password" : "Show password"
                }
              >
                {showPw ? (
                  <EyeOff size={16} />
                ) : (
                  <Eye size={16} />
                )}
              </button>
            }
          />

          <div className="mt-3 flex justify-end">
            <Link
              to="/forgot-password"
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Forgot password?
            </Link>
          </div>
        </div>

        {error && (
          <p className="text-xs text-primary">
            {error}
          </p>
        )}

        <button
          type="submit"
          data-testid="login-submit"
          disabled={submitting}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3.5 text-sm font-medium text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
        >
          {submitting ? "Signing in…" : "Log in"}
          <ArrowRight size={16} />
        </button>

        <p className="pt-4 text-center text-sm text-muted-foreground">
          Don't have an account?{" "}
          <Link
            to="/register"
            className="font-medium text-foreground underline"
          >
            Create one
          </Link>
        </p>
      </form>
    </AuthShell>
  );
};

export const Field = ({
  label,
  type = "text",
  value,
  onChange,
  testId,
  rightSlot,
}) => (
  <label className="block">
    <span className="block text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
      {label}
    </span>

    <span className="mt-3 flex items-center gap-3 border-b border-foreground/20 focus-within:border-foreground">
      <input
        type={type}
        value={value}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 bg-transparent py-3 text-base text-foreground placeholder:text-muted-foreground focus:outline-none"
      />

      {rightSlot}
    </span>
  </label>
);

export default Login;